import type { PersonalizedTasksStatus } from '@change-corgi/middle-end-gql-schema';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';

import { PERSONALIZED_TASKS_GENERATION_STATUSES } from '../../constants';
import type { DynamicTaskData, DynamicTaskShape, DynamicTaskShapeCore } from '../../types';

type PersonalizedTasksResponse = StarterDashboardV2PageData['petition']['personalizedTasks'];

export function parsePersonalizedTasks(personalizedTasks: PersonalizedTasksResponse): {
	generationStatus: PersonalizedTasksStatus;
	tasks: readonly DynamicTaskData[];
} {
	if (personalizedTasks.__typename === 'PersonalizedTasksSuccess') {
		return {
			generationStatus: personalizedTasks.status,
			tasks: personalizedTasks.tasks,
		};
	}
	return { generationStatus: PERSONALIZED_TASKS_GENERATION_STATUSES.FAILED, tasks: [] };
}

type Petition = StarterDashboardV2PageData['petition'];

export type PetitionTaskNode = Extract<
	Petition['activeTasks'],
	{ readonly __typename: 'PetitionTasksConnection' }
>['nodes'][number];

export function isKnownStarterTaskCompletedOnPetition(petition: Petition, featureKey: string): boolean {
	const { allCompletedTasks, activeTasks } = petition;

	if (allCompletedTasks?.__typename === 'PetitionTasksConnection') {
		if (
			allCompletedTasks.nodes.some(
				(petitionTask: PetitionTaskNode) => petitionTask.name === featureKey && petitionTask.status === 'COMPLETED',
			)
		) {
			return true;
		}
	}

	if (activeTasks?.__typename === 'PetitionTasksConnection') {
		const match = activeTasks.nodes.find((petitionTask: PetitionTaskNode) => petitionTask.name === featureKey);
		if (match?.status === 'COMPLETED') return true;
	}

	return false;
}

function getFirstActiveTaskIndex(tasks: readonly DynamicTaskData[]): number {
	return tasks.findIndex((task) => task.status === 'ACTIVE');
}

function toDynamicTaskShape(task: DynamicTaskData, index: number, firstActiveTaskIndex: number): DynamicTaskShapeCore {
	return {
		...task,
		index,
		isFirstActive: index === firstActiveTaskIndex,
	};
}

export function buildTaskShapes(tasks: readonly DynamicTaskData[]): readonly DynamicTaskShapeCore[] {
	const firstActiveTaskIndex = getFirstActiveTaskIndex(tasks);
	const shapes = tasks.map((task, index) => toDynamicTaskShape(task, index, firstActiveTaskIndex));
	return [...shapes].sort((leftShape, rightShape) => {
		const leftRank = leftShape.status === 'COMPLETED' ? 1 : 0;
		const rightRank = rightShape.status === 'COMPLETED' ? 1 : 0;
		return leftRank - rightRank;
	});
}

export function getDisplayedTasks(tasks: readonly DynamicTaskShape[], count: number): readonly DynamicTaskShape[] {
	return tasks.slice(0, count);
}

export function getTaskStats(tasks: ReadonlyArray<Readonly<{ uiCompleted: boolean }>>): Readonly<{
	completed: number;
	count: number;
}> {
	const completed = tasks.filter((task) => task.uiCompleted).length;
	return { completed, count: tasks.length };
}

export function orderDynamicTasksForDisplay(mapped: readonly DynamicTaskShape[]): DynamicTaskShape[] {
	const sorted = [...mapped].sort((leftRow, rightRow) => {
		const leftRank = leftRow.uiCompleted ? 1 : 0;
		const rightRank = rightRow.uiCompleted ? 1 : 0;
		return leftRank - rightRank;
	});
	const firstIncompleteId = sorted.find((row) => !row.uiCompleted)?.id ?? null;
	return sorted.map((task, displayIndex) => ({
		...task,
		index: displayIndex,
		isFirstActive: task.id === firstIncompleteId,
	}));
}
